import { Three2D } from 'redhotmagma-visualization/Three/Three2D';
import { setOrthographicCameraSize } from 'redhotmagma-visualization/Utils/setOrthographicCameraSize';
import { THREE } from 'redhotmagma-visualization/ExposedModules';

const { Vector2, Vector3, Box3 } = THREE;

export class Three2dVariant extends Three2D {
  designAreaScreenshotMode = false;

  // fix from the core
  async getDrawingContext() {
    this._redraw = this._getDrawingContext.bind(this, ...arguments);
    try {
      await this._redrawPromise;
    } catch (e) {
      // continue drawing anyway
    }
    this._redrawPromise = new Promise((resolve, reject) => {
      this._redrawFrame = window.requestAnimationFrame(async () => {
        try {
          await this.redraw();
          resolve();
        } catch (e) {
          reject(e);
        } finally {
          delete this._redrawPromise;
        }
      });
    });
    await this._redrawPromise;
  }

  getScreenshots(...args) {
    return this.designAreaScreenshotMode
      ? this.getDesignAreaScreenshots(...args)
      : super.getScreenshots(...args);
  }

  calculateZoomForObjectScreenshot(obj) {
    if (!obj) {
      return this.getDefaultZoom();
    }
    const { min, max } = new Box3().setFromObject(obj);
    const { x: w, y: h } = max.sub(min);

    const { width: cWidth, height: cHeight } = this.renderer.getSize(new Vector2());

    const areaFitZoom = Math.max(cWidth / w, cHeight / h);
    const canvasRelativeZoom = this.getZoomForSize(this.renderer.getSize(new Vector2())) * 2;
    return Math.max(canvasRelativeZoom, Math.min(areaFitZoom * 0.6, 2));
  }

  async jumpToAreaScreenshot(designArea, camera = this.camera) {
    if (!this._containerGroup) {
      // eslint-disable-next-line no-console
      console.log(`Container group is not available, skipping jump to view!`);
      return false;
    }

    const obj = designArea && this.findAreaObjectByName(designArea.identifier);
    const zoom = this.calculateZoomForObjectScreenshot(obj);

    const position = new Vector3();
    if (obj) {
      obj.getWorldPosition(position);
    }
    position.z = camera.position.z;

    camera.position.copy(position);
    camera.zoom = zoom;
    camera.updateProjectionMatrix();
    return true;
  }

  async getDesignAreaScreenshots(callback, { areaList, variantList, size }) {
    if (!this.renderer) {
      return callback({});
    }
    this._screenshottingInProgress = true;
    this.endAnimation();
    await this.animationRunning();
    const removeTempOverlay = await this.createTempOverlay();
    const origDesignView = this._currentDesignView;

    // if the areaList option is provided, use it to filter the design areas
    const designAreaList =
      areaList instanceof Array && areaList.length > 0
        ? this._designAreaList.filter((designArea) => areaList.indexOf(designArea.identifier) !== -1)
        : this._designAreaList;

    const variants =
      variantList instanceof Array && variantList.length > 0 ? variantList : [this._materialVariantPath];

    const screenshots = {};

    // store the original camera, variant and renderer size
    const origVariantPath = this._materialVariantPath;
    const origSize = new Vector2();
    this.renderer.getSize(origSize);
    const origPixelRatio = this.renderer.getPixelRatio();

    const restoreOriginalValues = async () => {
      this._screenshotCamera = null;
      this._screenshottingInProgress = false;
      this._materialVariantPath = origVariantPath;
      this._currentDesignView = null;
      await this.setView(origDesignView);
      this.renderer.setSize(origSize.width, origSize.height);
      this.renderer.setPixelRatio(origPixelRatio);
      this.render();
      removeTempOverlay();
    };

    const camera = this.createCameraBase();
    this._screenshotCamera = camera;

    for (let variant of variants) {
      const data = {};
      this.setMaterialVariantPath(variant);
      this._currentDesignView = null;

      // take a fit-sized (normal) screenshot of the default design view
      if (this.defaultDesignView) {
        await this.updateView(this.defaultDesignView, {
          forceUpdate: false,
          updateMaterial: false,
        });

        const { width, height } = this.getScreenshotSize(size);
        setOrthographicCameraSize(camera, width, height);

        this.renderer.setSize(width, height);
        this.renderer.setPixelRatio(1);
        camera.zoom = this.getZoomForSize({ width, height });
        camera.updateProjectionMatrix();

        await this.dispatchMaterialUpdateEvent({ screenshot: true });

        // set size and pixel ratio right before rendering, to make sure they are correct
        this.renderer.setSize(width, height);
        this.renderer.setPixelRatio(1);
        this.renderer.render(this.scene, camera);

        data['default'] = this.renderer.domElement.toDataURL();
      }

      // take a closer look screenshot of each design areas
      for (let designArea of designAreaList) {
        if (this._abortScreenshotting) {
          return await restoreOriginalValues();
        }

        try {
          await new Promise((resolve) => {
            window.setTimeout(async () => {
              await this.updateView(designArea.designView, {
                forceUpdate: false,
                updateMaterial: false,
              });

              const { width, height } = this.getScreenshotSize(size);
              setOrthographicCameraSize(camera, width, height);

              this.renderer.setSize(width, height);
              this.renderer.setPixelRatio(1);
              camera.updateProjectionMatrix();

              await this.jumpToAreaScreenshot(designArea, camera);
              await this.dispatchMaterialUpdateEvent({ screenshot: true });
              this.renderer.render(this.scene, camera);

              data[designArea.identifier] = this.renderer.domElement.toDataURL();
              resolve();
            });
          });
        } catch (e) {
          // ignore errors
        }
      }
      screenshots[variant] = data;
    }

    await restoreOriginalValues();

    callback(screenshots);
  }
}

export default Three2dVariant;
